import { useAuth, useUser } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import { useEffect, useRef, useState } from "react";

import { useCreatePasskey } from "../../hooks/useCreatePasskey";
import {
  decodePasskeyPromptState,
  fetchAppState,
  putPasskeyPromptState,
  type PasskeyPromptState,
  type PasskeyPromptTrigger,
} from "../../lib/app-state-api";
import {
  captureAuthPasskeyDeclined,
  captureAuthPasskeyEnrolled,
  captureAuthPasskeyPrompted,
} from "../../lib/auth-analytics";
import { getBrowserPathname, isDevPreviewPath } from "../../lib/dev-preview";
import { Button } from "../ui/button";
import { useToast } from "../ui/toast";

export type PasskeyPromptDeclineReason = "dismissed" | "cancelled" | "error";

export interface PasskeyEnrollPromptProps {
  trigger: PasskeyPromptTrigger;
  className?: string;
  onShown?: () => void;
  onEnrolled?: () => void;
  onDeclined?: (reason: PasskeyPromptDeclineReason) => void;
  /**
   * Fires once when the prompt has resolved that it will NOT render (passkey
   * exists, unsupported, dismissed, preview path, unreadable state). Lets a
   * host that holds a screen open for the prompt move on without waiting.
   */
  onSuppressed?: () => void;
}

// Triggers whose ceremony errored this page session — "do not re-prompt this
// session" (design §6.1). Module-level so a remount cannot re-open it.
const erroredThisSession = new Set<PasskeyPromptTrigger>();

export function resetPasskeyPromptSessionForTests(): void {
  erroredThisSession.clear();
}

function webAuthnSupported(): boolean {
  return typeof PublicKeyCredential !== "undefined";
}

/**
 * Post-success passkey enrollment nudge (design §6.1). Render it only AFTER
 * the surrounding action (Plaid link / publish) has succeeded. Self-gating:
 * renders nothing unless the user has no passkey, the browser supports
 * WebAuthn, this trigger was never dismissed (per-account, `user_app_state`
 * `consumer.auth/passkey_prompt.v1`), and we are not on a dev-preview path.
 */
export function PasskeyEnrollPrompt(props: PasskeyEnrollPromptProps) {
  // The Clerk hooks live in the inner component so dev-preview paths (where
  // ClerkProvider is bypassed) never call them.
  if (isDevPreviewPath(getBrowserPathname()) || !webAuthnSupported()) {
    return <Suppressed onSuppressed={props.onSuppressed} />;
  }
  return <PromptInner {...props} />;
}

function Suppressed({ onSuppressed }: { onSuppressed?: () => void }) {
  useEffect(() => {
    onSuppressed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function PromptInner({
  trigger,
  className,
  onShown,
  onEnrolled,
  onDeclined,
  onSuppressed,
}: PasskeyEnrollPromptProps) {
  const { isLoaded, user } = useUser();
  const { getToken } = useAuth();
  const { pushToast } = useToast();
  const createPasskey = useCreatePasskey();
  const posthog = usePostHog();
  const capture = (event: string, props?: Record<string, unknown>) =>
    posthog?.capture?.(event, props);

  const [stored, setStored] = useState<PasskeyPromptState | null>(null);
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownFired = useRef(false);
  // Read once at mount so recording an error doesn't hide the inline message.
  const [erroredEarlier] = useState(() => erroredThisSession.has(trigger));

  const suppressedFired = useRef(false);
  function suppress() {
    // Once shown, the prompt is the host's to wait on — e.g. enrolling flips
    // `user.passkeys`, which must not read as "suppressed".
    if (suppressedFired.current || shownFired.current) return;
    suppressedFired.current = true;
    onSuppressed?.();
  }

  const hasPasskey = (user?.passkeys?.length ?? 0) > 0;
  const blocked =
    !isLoaded || !user || hasPasskey || erroredEarlier;

  useEffect(() => {
    if (isLoaded && (!user || hasPasskey || erroredEarlier)) suppress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, user, hasPasskey, erroredEarlier]);

  useEffect(() => {
    if (blocked) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        if (!token) {
          if (!cancelled) suppress();
          return;
        }
        const res = await fetchAppState(token);
        if (cancelled) return;
        setStored(
          decodePasskeyPromptState(res.app_state?.["consumer.auth"]?.["passkey_prompt.v1"]),
        );
      } catch {
        // Fail closed: an unreadable dismissal must not become a nag.
        if (!cancelled) suppress();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked]);

  const visible =
    !blocked && !closed && stored !== null && stored[trigger] === undefined;

  useEffect(() => {
    if (stored !== null && stored[trigger] !== undefined) suppress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored]);

  useEffect(() => {
    if (visible && !shownFired.current) {
      shownFired.current = true;
      captureAuthPasskeyPrompted(capture, trigger);
      onShown?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  async function recordDismissal() {
    try {
      const token = await getToken();
      if (!token) return;
      await putPasskeyPromptState(
        { ...(stored ?? {}), [trigger]: new Date().toISOString() },
        token,
      );
    } catch {
      // Best-effort: worst case is one more impression.
    }
  }

  function decline(reason: PasskeyPromptDeclineReason) {
    setClosed(true);
    captureAuthPasskeyDeclined(capture, trigger, reason);
    onDeclined?.(reason);
  }

  async function handleSetUp() {
    setBusy(true);
    setError(null);
    const result = await createPasskey();
    setBusy(false);
    if (result.status === "enrolled") {
      pushToast({
        title: "Passkey added",
        description: "Next time, sign in with your face, fingerprint, or device PIN.",
      });
      setClosed(true);
      captureAuthPasskeyEnrolled(capture, trigger);
      onEnrolled?.();
      return;
    }
    if (result.status === "cancelled") {
      void recordDismissal();
      decline("cancelled");
      return;
    }
    erroredThisSession.add(trigger);
    setError(result.message);
    captureAuthPasskeyDeclined(capture, trigger, "error");
    onDeclined?.("error");
  }

  function handleNotNow() {
    void recordDismissal();
    decline("dismissed");
  }

  if (!visible) return null;

  return (
    <section
      aria-label="Set up a passkey"
      className={`surface-muted space-y-3 p-4 ${className ?? ""}`.trim()}
      data-testid="passkey-enroll-prompt"
    >
      <p className="text-sm text-foreground">
        Skip the code next time. Sign in with your face, fingerprint, or device PIN.
      </p>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {error ? (
          <Button onClick={() => setClosed(true)} size="sm" variant="outline">
            Close
          </Button>
        ) : (
          <>
            <Button disabled={busy} onClick={() => void handleSetUp()} size="sm">
              Set up
            </Button>
            <Button
              disabled={busy}
              onClick={handleNotNow}
              size="sm"
              variant="ghost"
            >
              Not now
            </Button>
          </>
        )}
      </div>
    </section>
  );
}
